import { z } from 'zod';

/**
 * SUPPORT-1634 wire contracts. The scenario fixtures and the server_pray routes must both satisfy
 * these schemas (`test:contracts`), so a simulated screen can never show a shape the server will
 * not send. Wire is snake_case; decoders hand the app camelCase.
 */

export const offerStatusSchema = z.enum(['active', 'inactive', 'deleted']);

export const offerWireSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  status: offerStatusSchema,
  cover_image_url: z.string().nullable(),
  file_size: z.number().nullable(),
  edition: z.number().int().positive(),
  replaces_offer_id: z.string().nullable(),
  created_at: z.string(),
});

export const offerUsageWireSchema = z.object({
  usage_type: z.enum(['campaign', 'automation', 'email']),
  ref_id: z.string(),
  ref_name: z.string(),
  ref_status: z.string(),
  // The offer the reference actually delivers. Differs from the queried offer when an email pins an older edition.
  delivered_offer_id: z.string(),
  delivered_offer_title: z.string(),
});

export const paginationWireSchema = z.object({
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
});

export const offerUsagesResponseSchema = z.object({
  data: z.array(offerUsageWireSchema),
  pagination: paginationWireSchema,
});

export const editionStatsWireSchema = z.object({
  offer_id: z.string(),
  edition: z.number().int().positive(),
  title: z.string(),
  downloads: z.number().int().nonnegative(),
  enrolled_leads: z.number().int().nonnegative(),
});

export const offerEditionsResponseSchema = z.object({ data: z.array(editionStatsWireSchema) });

export const campaignOfferWireSchema = z.object({
  id: z.string(),
  name: z.string(),
  offer_id: z.string().nullable(),
  offer: offerWireSchema.nullable(),
  capabilities: z.object({ can_change_offer: z.boolean() }),
  automations: z.array(z.object({ id: z.string(), name: z.string(), status: z.string() })),
  enrollments_by_offer: z.array(z.object({ offer_id: z.string(), title: z.string(), count: z.number().int() })),
});

export const campaignOfferResponseSchema = z.object({ data: campaignOfferWireSchema });

export const emailOfferSourceSchema = z.enum(['campaign', 'pinned', 'none']);

export const automationOfferCheckWireSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  trigger: z.object({
    campaign_id: z.string().nullable(),
    campaign_name: z.string().nullable(),
    campaign_status: z.enum(['active', 'deleted']).nullable(),
    campaign_offer: offerWireSchema.nullable(),
  }),
  emails: z.array(
    z.object({
      id: z.string(),
      subject: z.string(),
      status: z.string(),
      offer_source: emailOfferSourceSchema,
      // Kept when the offer is gone, so a deleted pin can still be repointed.
      pinned_offer_id: z.string().nullable(),
      pinned_offer: offerWireSchema.nullable(),
    })
  ),
});

export const automationOfferCheckResponseSchema = z.object({ data: automationOfferCheckWireSchema });

export const leadFunnelResultWireSchema = z.object({
  campaign_id: z.string(),
  automation_id: z.string(),
  approval_status: z.enum(['live', 'pending_approval', 'rejected']),
});

export const leadFunnelResponseSchema = z.object({ data: leadFunnelResultWireSchema });

export const offersListResponseSchema = z.object({ data: z.array(offerWireSchema) });

export const offerResponseSchema = z.object({ data: offerWireSchema });

export type OfferWire = z.infer<typeof offerWireSchema>;
export type OfferUsageWire = z.infer<typeof offerUsageWireSchema>;
export type CampaignOfferWire = z.infer<typeof campaignOfferWireSchema>;
export type AutomationOfferCheckWire = z.infer<typeof automationOfferCheckWireSchema>;
export type EditionStatsWire = z.infer<typeof editionStatsWireSchema>;
export type LeadFunnelResultWire = z.infer<typeof leadFunnelResultWireSchema>;
